import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CalendarDays, Clock3, MapPin, Plus, Trash2 } from 'lucide-react';
import { getApiUrl } from '../../config/api';
import './SchoolCalendar.css';

interface CalendarEvent {
  id: string;
  name: string;
  date: string;
  endDate?: string | null;
  description?: string;
  location?: string;
  category?: string;
  responsibleTeacher?: string;
}

interface AcademicTerm {
  id: number;
  name?: string | null;
  term?: number | string;
  year?: number | string;
  startDate?: string | null;
  endDate?: string | null;
}

interface CalendarResponse {
  schoolName: string;
  events: CalendarEvent[];
  terms: AcademicTerm[];
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Date not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date not set';
  return date.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const SchoolCalendar: React.FC = () => {
  const role = (localStorage.getItem('role') || '').toLowerCase();
  const token = localStorage.getItem('token') || '';
  const canManageCalendar = role === 'admin' || role === 'headteacher' || role === 'superadmin';

  const [data, setData] = useState<CalendarResponse>({ schoolName: 'School', events: [], terms: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    name: '',
    date: '',
    endDate: '',
    location: '',
    category: 'general',
    responsibleTeacher: '',
    description: ''
  });

  const fetchCalendar = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get<CalendarResponse>(getApiUrl('school/calendar'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (err: any) {
      console.error('Error fetching school calendar:', err);
      setError(err.response?.data?.message || 'Failed to load school calendar');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return data.events.filter((event) => new Date(event.date) >= now).slice(0, 4);
  }, [data.events]);

  const groupedEvents = useMemo(() => {
    return data.events.reduce<Record<string, CalendarEvent[]>>((acc, event) => {
      const monthKey = new Date(event.date).toLocaleDateString([], { month: 'long', year: 'numeric' });
      if (!acc[monthKey]) acc[monthKey] = [];
      acc[monthKey].push(event);
      return acc;
    }, {});
  }, [data.events]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await axios.post(
        getApiUrl('school/calendar/events'),
        {
          name: form.name.trim(),
          date: form.date,
          endDate: form.endDate || null,
          location: form.location.trim(),
          category: form.category,
          responsibleTeacher: form.responsibleTeacher.trim(),
          description: form.description.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Calendar event saved successfully.');
      setForm({
        name: '',
        date: '',
        endDate: '',
        location: '',
        category: 'general',
        responsibleTeacher: '',
        description: ''
      });
      await fetchCalendar();
    } catch (err: any) {
      console.error('Error saving calendar event:', err);
      setError(err.response?.data?.message || 'Failed to save calendar event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId: string) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await axios.delete(getApiUrl(`school/calendar/events/${eventId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Calendar event deleted successfully.');
      await fetchCalendar();
    } catch (err: any) {
      console.error('Error deleting calendar event:', err);
      setError(err.response?.data?.message || 'Failed to delete calendar event');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="school-calendar-page">
        <div className="school-calendar-empty">Loading school calendar…</div>
      </div>
    );
  }

  return (
    <div className="school-calendar-page">
      <div className="school-calendar-hero">
        <div>
          <p className="school-calendar-eyebrow">School Calendar</p>
          <h1>{data.schoolName} Calendar</h1>
          <p>Track upcoming school events, academic milestones, and important term dates in one place.</p>
        </div>
        <div className="school-calendar-hero-stats">
          <div>
            <span>Upcoming events</span>
            <strong>{upcomingEvents.length}</strong>
          </div>
          <div>
            <span>Academic terms</span>
            <strong>{data.terms.length}</strong>
          </div>
        </div>
      </div>

      {error ? <div className="school-calendar-feedback error">{error}</div> : null}
      {success ? <div className="school-calendar-feedback success">{success}</div> : null}

      <div className="school-calendar-grid">
        <section className="school-calendar-card">
          <div className="school-calendar-card-header">
            <div>
              <p className="school-calendar-eyebrow">Highlights</p>
              <h2>Upcoming Events</h2>
            </div>
          </div>
          {upcomingEvents.length === 0 ? (
            <div className="school-calendar-empty">
              <CalendarDays size={26} />
              <p>No upcoming events have been published yet.</p>
            </div>
          ) : (
            <div className="school-calendar-event-list">
              {upcomingEvents.map((eventItem) => (
                <article key={eventItem.id} className="school-calendar-event">
                  <div className="school-calendar-event-date">
                    <strong>{new Date(eventItem.date).toLocaleDateString([], { day: '2-digit' })}</strong>
                    <span>{new Date(eventItem.date).toLocaleDateString([], { month: 'short' })}</span>
                  </div>
                  <div className="school-calendar-event-copy">
                    <h3>{eventItem.name}</h3>
                    <p>{eventItem.description || 'No event notes provided.'}</p>
                    <div className="school-calendar-event-meta">
                      <span><Clock3 size={14} /> {formatDate(eventItem.date)}</span>
                      {eventItem.location ? <span><MapPin size={14} /> {eventItem.location}</span> : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="school-calendar-card">
          <div className="school-calendar-card-header">
            <div>
              <p className="school-calendar-eyebrow">Academic Calendar</p>
              <h2>Term Dates</h2>
            </div>
          </div>
          {data.terms.length === 0 ? (
            <div className="school-calendar-empty">
              <CalendarDays size={26} />
              <p>No academic terms are configured yet.</p>
            </div>
          ) : (
            <div className="school-calendar-term-list">
              {data.terms.map((term) => (
                <div key={term.id} className="school-calendar-term">
                  <strong>{term.name || `Term ${term.term || ''}`.trim()}</strong>
                  <span>{term.year || ''}</span>
                  <p>{formatDate(term.startDate)} to {formatDate(term.endDate)}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {canManageCalendar ? (
        <section className="school-calendar-card">
          <div className="school-calendar-card-header">
            <div>
              <p className="school-calendar-eyebrow">Manage Calendar</p>
              <h2>Add Event</h2>
            </div>
          </div>

          <form className="school-calendar-form" onSubmit={handleSubmit}>
            <input name="name" value={form.name} onChange={handleChange} placeholder="Event title" required />
            <input name="date" type="date" value={form.date} onChange={handleChange} required />
            <input name="endDate" type="date" value={form.endDate} onChange={handleChange} />
            <input name="location" value={form.location} onChange={handleChange} placeholder="Location" />
            <select name="category" value={form.category} onChange={handleChange}>
              <option value="general">General</option>
              <option value="academic">Academic</option>
              <option value="exam">Exam</option>
              <option value="sports">Sports</option>
            </select>
            <input name="responsibleTeacher" value={form.responsibleTeacher} onChange={handleChange} placeholder="Responsible staff member" />
            <textarea name="description" value={form.description} onChange={handleChange} placeholder="Event notes" rows={4} />
            <button type="submit" className="school-calendar-primary-btn" disabled={saving}>
              <Plus size={16} />
              <span>{saving ? 'Saving…' : 'Save Event'}</span>
            </button>
          </form>
        </section>
      ) : null}

      <section className="school-calendar-card">
        <div className="school-calendar-card-header">
          <div>
            <p className="school-calendar-eyebrow">All Events</p>
            <h2>Calendar Timeline</h2>
          </div>
        </div>

        {data.events.length === 0 ? (
          <div className="school-calendar-empty">
            <CalendarDays size={26} />
            <p>No calendar events have been added yet.</p>
          </div>
        ) : (
          <div className="school-calendar-month-groups">
            {Object.entries(groupedEvents).map(([month, events]) => (
              <div key={month} className="school-calendar-month-group">
                <h3>{month}</h3>
                <div className="school-calendar-month-events">
                  {events.map((eventItem) => (
                    <article key={eventItem.id} className="school-calendar-month-event">
                      <div>
                        <strong>{eventItem.name}</strong>
                        <p>{formatDate(eventItem.date)}</p>
                        {eventItem.description ? <span>{eventItem.description}</span> : null}
                      </div>
                      <div className="school-calendar-month-actions">
                        {eventItem.location ? <small>{eventItem.location}</small> : null}
                        {canManageCalendar ? (
                          <button type="button" onClick={() => handleDelete(eventItem.id)} disabled={saving} aria-label={`Delete ${eventItem.name}`}>
                            <Trash2 size={16} />
                          </button>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SchoolCalendar;
